import { Add, CheckBox } from '@mui/icons-material';
import { Button, Card, Checkbox, CircularProgress, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Paper, Radio, RadioGroup, FormControlLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup, TextField } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react'
import ModalContext from '../../../context/ModalContext';
import AuthContext from '../../../context/AuthContext';
import './Location.scss';

export default function Location({ locationDetails, setLocationDetails, company, couponDetails, errors, setError, isPopin = false }) {

  const authCtx = useContext(AuthContext);

  const modalCtx = useContext(ModalContext);

  const [allSelected, setAllSelected] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchLocations();
    // Clear errors when component mounts - errors should only show when clicking "Next"
    setError('location', false);
    setError('cta', false);
  }, [])

  // Initialize default to "store" if not set
  useEffect(() => {
    if (locationDetails?.couponAvailability === undefined && locationDetails?.onlineOnly === undefined) {
      setLocationDetails(prev => ({
        ...prev,
        couponAvailability: "instore",
        onlineOnly: "",
        availabilityType: prev.availabilityType || "local"
      }));
    }
  }, [])

  // For popin coupons, force availability away from "online only"
  useEffect(() => {
    if (isPopin && locationDetails?.couponAvailability === "online") {
      setLocationDetails(prev => ({
        ...prev,
        couponAvailability: "instore",
        onlineOnly: "",
        availabilityType: "local"
      }));
    }
  }, [isPopin])


  // Debug: Log when locationDetails.locations actually updates
  useEffect(() => {
    console.log("📍 locationDetails.locations updated:", locationDetails.locations);
    console.log("📍 locationDetails.locations.length:", locationDetails.locations?.length);
  }, [locationDetails.locations]);

  useEffect(() => {
    if (isLoading === false && locationDetails.locations.length > 0 && locationDetails.checkedId.length > 0) {
      console.log("locationDetails", locationDetails);
      console.log("locationDetails.checkedId", locationDetails.checkedId);
      console.log("locationDetails.locations", locationDetails.locations);
      const validCheckedIds = locationDetails.checkedId.filter(id => 
        locationDetails.locations.some(loc => loc.id === id)
      );
      console.log("validCheckedIds", validCheckedIds);
      // Update checkedId if there are any invalid IDs
      if (validCheckedIds.length !== locationDetails.checkedId.length) {
        setLocationDetails(prev => ({
          ...prev,
          checkedId: validCheckedIds
        }));
      }
    }
  }, [isLoading, locationDetails.locations]);


  useEffect(() => {
    if (allSelected) {
      const allIDs = locationDetails.locations.map(lc => lc.id);
      setLocations(allIDs);
    }
    else{
      setLocations([])
    }
  }, [allSelected])

  const handleModal = (modal, data) => {
    modalCtx.openModal();
    modalCtx.setDetails(modal, data);
  }

  const setLocations = (_locations) => {
    // Clear location error if at least one location is selected
    if (_locations.length > 0) {
      setError('location', false);
    }
    setLocationDetails(prev => ({ ...prev, checkedId: _locations }))
  }

  const setCallToActions = (_cta) => {
    setLocationDetails(prev => ({ ...prev, callToActionButton: _cta }))
  }

  const toggleCTA = (_id) => {
    setError('cta', false)
    const currentIndex = locationDetails.callToActionButton.findIndex((item) => item.id == _id);
    const newList = [...locationDetails.callToActionButton];
    newList[currentIndex].checked = !newList[currentIndex].checked;
    setCallToActions(newList);
  }

  const setCouponAvailability = (type) => {
    setLocationDetails(prev => {
      let updatedCallToActionButton = [...(prev.callToActionButton || [])];
      
      if (type === "online") {
        // Online only: uncheck navigate button
        updatedCallToActionButton = updatedCallToActionButton.map(button => ({
          ...button,
          checked: button.name === 'Navigate Button' ? false : button.checked
        }));
        return { 
          ...prev, 
          onlineOnly: "yes",
          couponAvailability: "online",
          availabilityType: "local",
          callToActionButton: updatedCallToActionButton
        };
      } else if (type === "instore") {
        // Store only: enable navigate button
        return { 
          ...prev, 
          onlineOnly: "",
          couponAvailability: "instore",
          availabilityType: "local",
          callToActionButton: updatedCallToActionButton
        };
      } else if (type === "both") {
        // Both: keep navigate button as is
        return { 
          ...prev, 
          onlineOnly: "yes",
          couponAvailability: "both",
          availabilityType: prev.availabilityType || "global",
          callToActionButton: updatedCallToActionButton
        };
      }
      return prev;
    });
  }

  // Extract city and state from address
  const extractCityState = (address) => {
    if (!address) return { city: '', state: '' };
    const parts = address.split(',');
    if (parts.length >= 2) {
      return { city: parts[0].trim(), state: parts[1].trim() };
    }
    return { city: address, state: '' };
  };

  // Enrich location with state, city, countryCode, and title
  const enrichLocation = (location) => {
    const { city: extractedCity, state: extractedState } = extractCityState(location.address);
    return {
      ...location,
      title: location.title || '',
      state: location.state || extractedState || '',
      city: location.city || extractedCity || '',
      countryCode: location.countryCode || location.country || ''
    };
  };

  const addNewLocation = (_location) => {
    if (_location) {
      const enrichedLocation = enrichLocation(_location);
      
      // Use locationType from the saved location (set via checkbox in modal)
      // If not provided, set default based on coupon availability
      if (!enrichedLocation.locationType) {
        const couponType = locationDetails?.couponAvailability || (locationDetails?.onlineOnly === "yes" ? "online" : "instore");
        if (couponType === "instore") {
          enrichedLocation.locationType = "local";
        } else if (couponType === "online") {
          enrichedLocation.locationType = "regional"; // Online coupons need regional locations
        } else {
          enrichedLocation.locationType = "local"; // Default fallback
        }
      }
      
      let newList = [...locationDetails.locations, enrichedLocation];
      var newCheckedList = [...locationDetails.checkedId, _location.id];

      var _locationDetails = {
        ...locationDetails,
        checkedId: newCheckedList,
        locations: newList,
      };
      setLocationDetails(_locationDetails);
      modalCtx.closeModal();
    }
  }

  const fetchLocations = () => {
    if (company?.id) {
      try {
        fetch(`${process.env.REACT_APP_API_URI}/v3/admin/companies/${company.id}/locations`, {
          method: "GET",
          headers: { 'Content-Type': 'application/json', Authorization: "Bearer " + authCtx.token }
        }).then((res) => { return res.json() }).then((data) => {
          console.log(data);
          if (data.status == 'success') {
            // Enrich each location with state, city, countryCode, and title
            const enrichedLocations = data?.data?.map(loc => enrichLocation(loc)) || [];
            console.log("enrichedLocations", enrichedLocations);
            setLocationDetails(prev => {
              console.log("Previous locationDetails:", prev);
              console.log("New locations being set:", enrichedLocations);
              return { ...prev, locations: enrichedLocations };
            });
            setIsLoading(false);
          } else {
            console.error(data);
          }
        })
      } catch (e) {
        console.log(e);
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }

  const isActionDisabled = (actionName) => {
    // Check for email button
    if (actionName === 'Email Button') {
      const emailButton = locationDetails.callToActionButton.find(btn => btn.name === 'Email Button');
      if (emailButton && emailButton.checked) {
        return false; // Don't disable if it's already checked
      }
      
      // Disable if there's no email address
      const hasEmail = couponDetails?.emailAddress && couponDetails.emailAddress.trim() !== '';
      return !hasEmail;
    }
    
    // Check for call button
    if (actionName === 'Call Button') {
      const callButton = locationDetails.callToActionButton.find(btn => btn.name === 'Call Button');
      if (callButton && callButton.checked) {
        return false; // Don't disable if it's already checked
      }
      
      // Disable if there's no phone number
      const hasPhone = couponDetails?.phoneNumber && couponDetails.phoneNumber.trim() !== '';
      return !hasPhone;
    }

    // Check for website button
    if (actionName === 'Website Button') {
      const websiteButton = locationDetails.callToActionButton.find(btn => btn.name === 'Website Button');
      if (websiteButton && websiteButton.checked) {
        return false; // Don't disable if it's already checked
      }
      
      // Disable if there's no website URL
      const hasWebsite = couponDetails?.couponClaimUrl && couponDetails.couponClaimUrl.trim() !== '';
      return !hasWebsite;
    }

    // Disable navigate button for online-only coupons (not for "both")
    if (actionName === 'Navigate Button') {
      const couponType = locationDetails?.couponAvailability || (locationDetails?.onlineOnly === "yes" ? "online" : "instore");
      return couponType === "online";
    }
    
    return false;
  };

  // Check if location selection is required
  const isLocationRequired = () => {
    const couponType = locationDetails?.couponAvailability || (locationDetails?.onlineOnly === "yes" ? "online" : "instore");
    // Show locations for "store" or "both"
    // Also show locations for "online" when availabilityType is "local" (selected countries)
    if (couponType === "instore" || couponType === "both") {
      return true;
    }
    if (couponType === "online" && locationDetails?.availabilityType === "local") {
      return true;
    }
    return false;
  };
  
  // Check if toggle buttons should be shown
  const shouldShowToggleButtons = () => {
    const couponType = locationDetails?.couponAvailability || (locationDetails?.onlineOnly === "yes" ? "online" : "instore");
    // Show toggle buttons for "online" or "both"
    return couponType === "online" || couponType === "both";
  };

  if (isLoading) {
    return (
      <div className='create-coupon-section-v2' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className='create-coupon-section-v2'>
      <div className='horizontal-row-card-container'>
        <Card className='card-container' sx={{ width: '60%' }}>
          <span className='title'>Call to Action Buttons</span>
          <div style={{ display: 'flex', gap: '20px', width: '100%' }}>
            <div style={{ width: '50%' }}>
              <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                {(locationDetails?.callToActionButton || []).map((value) => {
                  const labelId = `checkbox-list-label-${value.id}`;
                  const isDisabled = isActionDisabled(value.name);                
                  return (
                    <ListItem
                      key={value.id}
                      secondaryAction={
                        <IconButton edge="end" aria-label="comments">
                        </IconButton>
                      }
                      disablePadding
                    >
                      <ListItemButton 
                        role={undefined} 
                        onClick={() => !isDisabled && toggleCTA(value.id)} 
                        dense
                        disabled={isDisabled}
                        sx={{
                          opacity: isDisabled ? 0.5 : 1,
                          cursor: isDisabled ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={value.checked}
                            tabIndex={-1}
                            disableRipple
                            disabled={isDisabled}
                            inputProps={{ 'aria-labelledby': labelId }}
                          />
                        </ListItemIcon>
                        <ListItemText id={labelId} primary={`${value.name}`} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </div>
            <div style={{ width: '50%', display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <RadioGroup
                className="coupon-availability-radio-group"
                value={locationDetails?.couponAvailability || ((locationDetails?.onlineOnly === "yes" && !locationDetails?.couponAvailability) ? "online" : "instore")}
                onChange={(e) => setCouponAvailability(e.target.value)}
              >
                <FormControlLabel
                  value="instore"
                  control={<Radio className="coupon-availability-radio" />}
                  label="Coupon is only available at location in store"
                  className="coupon-availability-option"
                />
                {!isPopin && (
                  <FormControlLabel
                    value="online"
                    control={<Radio className="coupon-availability-radio" />}
                    label="Coupon is available online only"
                    className="coupon-availability-option"
                  />
                )}
                <FormControlLabel
                  value="both"
                  control={<Radio className="coupon-availability-radio" />}
                  label="Coupon is available both online and in store"
                  className="coupon-availability-option"
                />
              </RadioGroup>
              {shouldShowToggleButtons() && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '20px' }}>
                  <ToggleButtonGroup
                    value={locationDetails.availabilityType || 'local'}
                    exclusive
                    onChange={(e, value) => {
                      if (value !== null) {
                        setLocationDetails(prev => ({...prev, availabilityType: value}));
                      }
                    }}
                    className="toggle-button-group"
                  >
                    <ToggleButton value="global">
                      Coupons available globally.
                    </ToggleButton>
                    <ToggleButton value="local">
                      Coupons available at selected countries.
                    </ToggleButton>
                  </ToggleButtonGroup>
                  
                  {locationDetails.availabilityType === "local" && (
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#666',
                      backgroundColor: '#f5f5f5',
                      padding: '10px',
                      borderRadius: '4px',
                      border: '1px solid #e0e0e0'
                    }}>
                      The countries of the locations selected below will be included as countries of operations based on their coordinates and what country they are in.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {errors?.cta?.show && <div className="coupon-error-text">{errors.cta.message}</div>}
        </Card>
      </div>
      
      {isLocationRequired() && (
        <div className='horizontal-row-card-container'>
          <Card className='card-container' sx={{ width: '60%' }}>
            <div className='full-width location-header'>
              <div className='title-subtitle-container'>
                <span className='title'>Locations</span>
                <span className='subtitle'>Select the locations for your coupon.</span>
              </div>
              <TextField
                size="small"
                placeholder="Search locations"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ width: '32%' }}
              />
              <span className='checkbox-span'><Checkbox checked={allSelected} onChange={() => setAllSelected(as => !as)} sx={{
                '& .MuiSvgIcon-root': { fontSize: 20 }
              }} /> Check all locations</span>
            </div>

            {locationDetails.locations.length > 0 ? <TableContainer component={Paper}>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell width={64}></TableCell>
                    <TableCell width={200} >Title</TableCell>
                    <TableCell>Address</TableCell>

                  </TableRow>
                </TableHead>
                <TableBody>
                  {
                    locationDetails.locations
                      .filter((row) => {
                        const couponType = locationDetails.couponAvailability;
                        const locationType = row?.locationType;

                        // Availability filter (respect existing mode logic)
                        if (couponType === "online" && locationType !== "regional") return false;
                        if (couponType === "instore" && locationType !== "local") return false;

                        // Search filter
                        if (!searchTerm?.trim()) return true;
                        const term = searchTerm.toLowerCase();
                        const inTitle = row?.title?.toLowerCase().includes(term);
                        const inAddress = row?.address?.toLowerCase().includes(term);
                        return inTitle || inAddress;
                      })
                      .map((row) => {
                      const hasCoordinates = row.latitude !== null && row.longitude !== null;
                      const isChecked = locationDetails.checkedId.includes(row.id);
                      const requiresManualSync = row.manualSync === "yes";

                      let showLocation = "all";
                      if(row?.locationType === "regional" && locationDetails.couponAvailability === "online") {
                        showLocation = "regional";
                      } else if(row?.locationType === "local" && locationDetails.couponAvailability === "instore") {
                        showLocation = "local";
                      } else {
                        showLocation = "all";
                      }
                    
                      // Determine if row should be shown based on coupon availability
                      const shouldShowRow = () => {
                        const couponType = locationDetails.couponAvailability;
                        
                        // If coupon is "both", show all locations
                        if (couponType === "both") {
                          return true;
                        }
                        
                        // If coupon is "online", only show regional locations
                        if (couponType === "online") {
                          return showLocation === "regional";
                        }
                        
                        // If coupon is "store", only show local locations
                        if (couponType === "instore") {
                          return showLocation === "local";
                        }
                        
                        // Default: show all
                        return true;
                      };
                      
                      // Don't render row if it shouldn't be shown
                      if (!shouldShowRow()) {
                        return null;
                      }
                    
                      return <TableRow 
                        key={row.id} 
                        sx={{ 
                          '&:last-child td, &:last-child th': { border: 0 },
                          opacity: requiresManualSync ? 0.5 : 1,
                          backgroundColor: requiresManualSync ? "#f5f5f5" : "transparent"
                        }}
                        className={requiresManualSync ? "manual-sync-disabled" : ""}
                      >
                        <TableCell width={50}>
                          <Checkbox sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }} edge="start"
                            checked={isChecked}
                            tabIndex={-1}
                            disableRipple
                            disabled={!hasCoordinates || requiresManualSync}
                            onChange={() => {
                              if (!hasCoordinates || requiresManualSync) return;                              
                              let newList;
                              if (locationDetails.checkedId.includes(row.id)) {
                                newList = locationDetails.checkedId.filter((item) => item != row.id);
                              } else {
                                newList = [...locationDetails.checkedId, row.id];
                              }
                              setLocations(newList);
                            }} 
                          />
                        </TableCell>
                        <TableCell width={200}>
                          <div className="location-title-wrapper">
                            {row.title}
                            {row?.locationType === "regional" && (
                              <span className="regional-tag">Regional</span>
                            )}
                          </div>
                          {!hasCoordinates && (
                            <div style={{ fontSize: '12px', color: '#d32f2f', fontStyle: 'italic', marginTop: '2px' }}>Coordinates incomplete</div>
                          )}
                        </TableCell>
                        <TableCell component="th" >{row.address}</TableCell>
                      </TableRow>
                    })
                  }
                </TableBody>
              </Table>
            </TableContainer> : <div>
              <span>Please add a Location First</span>
            </div>}

            {errors?.location?.show && <div className="coupon-error-text">{errors.location.message}</div>}


          </Card>
          <Card className='card-container center-card' sx={{ width: '40%' }}>
            <img src='https://admin.delivait.com/assets/img/location.png' />
            <span className='title'>Don't you have a registered Location?</span>
            <Button onClick={() => handleModal("add-location-modal", { addHandler: addNewLocation, company: company,locationDetails: locationDetails })} variant="outlined"><Add /> Add a New Location</Button>
          </Card>
        </div>
      )}

      {!isLocationRequired() && shouldShowToggleButtons() && (
        <div className='horizontal-row-card-container'>
          <Card className='card-container' sx={{ width: '100%', textAlign: 'center', padding: '2rem' }}>
            <span className='title'>Online-Only Global Coupon</span>
            <p style={{ color: '#666', marginTop: '1rem' }}>
              This coupon is available online globally, so no specific location selection is required.
            </p>
          </Card>
        </div>
      )}

    </div>
  )
}
